import useSafeFetch from 'shared/hooks/useSafeFetch';
import React, { useState, useRef, useEffect } from 'react';
import GiftCardOffering from 'giftcards/common/types/GiftCardOffering';

interface GiftCardOfferingsContextType {
  offerings: GiftCardOffering[] | undefined;
  setup: boolean;
  refreshOfferings: () => void;
  loading: boolean;
  error: boolean;
}

export const OfferingsContext = React.createContext<GiftCardOfferingsContextType>({
  offerings: [],
  setup: false,
  refreshOfferings: () => null,
  error: false,
  loading: false
});

interface State {
  offerings: GiftCardOffering[] | undefined;
  setup: boolean;
  error: boolean;
}

const GiftCardOfferingsContextProvider: React.FC = props => {
  const [state, setState] = useState<State>({
    offerings: undefined,
    setup: false,
    error: false
  });
  const { get } = useSafeFetch();

  const refreshOfferings = useRef(async () => {
    if (state.offerings !== undefined || state.error) {
      setState({ setup: false, offerings: undefined, error: false });
    }

    try {
      const response = await get(`${process.env.REACT_APP_PORTAL}/offerings`);
      const offerings = await response.json();
      const setup = Boolean(offerings?.length) && !(offerings?.[0].configured === false);

      setState(s => ({ ...s, offerings, setup }));
    } catch (e) {
      console.error(e);
      if (!e.cancelled && !e.unauthenticated) {
        setState(s => ({ ...s, error: true }));
      }
    }
  });

  useEffect(() => {
    refreshOfferings.current();
  }, []);

  return (
    <OfferingsContext.Provider
      value={{
        offerings: state.offerings,
        setup: state.setup,
        refreshOfferings: refreshOfferings.current,
        error: state.error,
        loading: state.offerings === undefined && !state.error
      }}
    >
      {props.children}
    </OfferingsContext.Provider>
  );
};

export default GiftCardOfferingsContextProvider;
